import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Building2, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { useImmobiliPortalAuth } from "../context/ImmobiliPortalAuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import AreaLoginShell from "../components/AreaLoginShell";

const MARK_URL = "/images/logo-immobiliare-mark.png";
const LOCKUP_URL = "/images/logo-immobiliare.png";

export default function ImmobiliPortalLogin() {
  const { login, isAuthenticated, loading } = useImmobiliPortalAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && isAuthenticated) {
    return <Navigate to="/portale-immobiliare" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AreaLoginShell
      accent="blue"
      currentArea="immobiliare"
      title="Area dedicata alle opportunità immobiliari"
      subtitle="Consulta schede, foto e planimetrie e invia richieste al referente, senza accedere al resto di Affidya."
      logo={
        <img
          src={LOCKUP_URL}
          alt="Immobiliare by Affidya"
          className="mx-auto mb-3 h-10 w-auto lg:mx-0 lg:mb-9 lg:h-12"
        />
      }
      features={[
        {
          icon: Building2,
          title: "Solo consultazione",
          text: "Immobili, canoni e trattative in un'area riservata.",
        },
        {
          icon: ShieldCheck,
          title: "Accesso dedicato",
          text: "Account creati dall'amministratore Affidya.",
        },
      ]}
    >
      <div className="mb-6 text-center">
        <img src={MARK_URL} alt="Immobiliare by Affidya" className="mx-auto mb-3 h-12 w-12 rounded-2xl object-contain" />
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">Accedi al portale</h2>
        <p className="mt-1 text-sm text-slate-500">Usa le credenziali ricevute da Affidya</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="portal-email" className="text-sm font-medium text-slate-700">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="portal-email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              placeholder="nome@esempio.it"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-sm focus-visible:ring-[#4068E8]/30"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="portal-password" className="text-sm font-medium text-slate-700">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="portal-password"
              type="password"
              required
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-sm focus-visible:ring-[#4068E8]/30"
            />
          </div>
        </div>

        {error ? (
          <p className="rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          disabled={submitting}
          className="h-11 w-full rounded-xl bg-[#4068E8] text-sm font-semibold text-white hover:bg-[#3557D4]"
        >
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Accedi
        </Button>
        <p className="text-center text-xs text-slate-500">
          Password dimenticata? Contatta l&apos;amministratore
        </p>
      </form>
    </AreaLoginShell>
  );
}
